// React and React Router
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { AppPresenter } from "./page/AppPresenter";
import { Flex, Loader } from "@mantine/core";

// Mantine
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";

// MUI
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import muiTheme from "./theme/muiTheme";

import { processUserLoginPure } from "./utils/pureDataFetch";
import { UserTypes } from "./contexts/UserContext";
import { installDebugTools } from "./utils/avatarDebugger";
import { setupGlobalAdminFunctions } from "./utils/adminSetup";
import { dbService } from './firebaseInstance';
import { arrayUnion } from 'firebase/firestore';

// Add this to imports
import { UserProvider, useUser, useUserDispatch } from "./contexts/UserContext";

// Remove moment import and add date-fns imports
import { format } from "date-fns";

// Add i18n import if not already present
import i18next from "i18next";
import "./i18n/config";

// Add this with other imports
import { useResetScroll } from "./hooks/useResetScroll";
import { initializeProjectFonts } from "./utils/fontCache";

// Add ThreadsContext imports
import {
  ThreadsProvider,
  useThreadsState,
  useThreadsDispatch,
  loadLifetimeDataWithTimeline,
} from "./contexts/ThreadsContext";
import { WritingModeProvider } from "./contexts/WritingModeContext";

// Add token status hook
import { useTokenStatus } from "./hooks/useTokenRefresh";

// Year End Prompt
import { YearEndPromptModal } from "./components/YearEnd";
import { useYearEndPrompt } from "./hooks/useYearEndPrompt";

function App() {
  // Add language handling
  useEffect(() => {
    const currentLang = i18next.language;
    if (currentLang.includes("en")) {
      i18next.changeLanguage("en");
    }
  }, []);

  // Initialize project fonts
  useEffect(() => {
    initializeProjectFonts();
  }, []);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <UserProvider>
        <ThreadsProvider>
          <WritingModeProvider>
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </WritingModeProvider>
        </ThreadsProvider>
      </UserProvider>
    </ThemeProvider>
  );

}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, isLogin, userLoading, error, hasYearEnd2025 } = useUser();
  const dispatch = useUserDispatch();
  const [isDashboardLoad, setIsDashboardLoad] = useState(false);
  const lifetimeLoadStartedRef = useRef(false);

  // 자동 토큰 상태 체크 훅 사용
  useTokenStatus();

  // 연말정산 프롬프트 훅
  const {
    shouldShow: shouldShowYearEndPrompt,
    isOpen: isYearEndPromptOpen,
    openPrompt: openYearEndPrompt,
    dismissPrompt: dismissYearEndPrompt,
    permanentlyDismiss: permanentlyDismissYearEndPrompt,
  } = useYearEndPrompt(2025);

  // OAuth code가 현재 URL에 붙어 돌아온 경우, 즉시 /callback으로 라우팅 전환
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    const redirect = params.get('redirect');
    // 테스트 모드에서는 콜백 강제 라우팅을 건너뜀
    let isLoginTestMode = false;
    try {
      isLoginTestMode = sessionStorage.getItem('login_test_mode') === '1';
    } catch (_) { }
    const isTestState = state === 'test';
    if (isLoginTestMode || isTestState) {
      // 테스트 모드: Login 페이지 상단 로직이 직접 처리
    } else if (code && state && location.pathname !== '/callback') {
      navigate({ pathname: '/callback', search: location.search }, { replace: true });
      return;
    }
    // SSR sharePage에서 전달한 redirect 파라미터 처리 (예: /?redirect=/collection/:id)
    if (redirect && typeof redirect === 'string') {
      try {
        const safePath = new URL(redirect, window.location.origin).pathname + new URL(redirect, window.location.origin).search;
        if (safePath.startsWith('/')) {
          navigate(safePath, { replace: true });
        }
      } catch (_) {
        // 무시
      }
    }
  }, [location.pathname, location.search, navigate]);



  // Store the current path in localStorage whenever it changes
  // useEffect(() => {
  // 	if (isLogin) {
  // 		localStorage.setItem('lastPath', location.pathname);
  // 	}
  // }, [location.pathname, isLogin]);

  // On component mount, check if there's a stored path and navigate to it
  // useEffect(() => {
  // 	const lastPath = localStorage.getItem('lastPath');
  // 	if (isLogin && lastPath && lastPath !== location.pathname) {
  // 		navigate(lastPath);
  // 	}
  // }, [isLogin, navigate]);

  // Dashboard states - 이제 Dashboard.js에서 timeline으로 관리됨
  // 기존 monthlyInsight는  이상 필요없음

  // Page Initial Loading & Setting
  // Dashboard
  useEffect(() => {
    if (profile !== null) {
      setIsDashboardLoad(true);
    }
  }, [profile]);

  // 최초 로드 시에만 사용자 데이터 로드 - tokenManager 활용
  useEffect(() => {
    const loadUserData = async () => {
      try {
        // OAuth 콜백 처리 중이면 세션 복원 스킵 (Callback에서 처리)
        const params = new URLSearchParams(location.search);
        const isTestState = params.get('state') === 'test';
        let isLoginTestMode = false;
        try { isLoginTestMode = sessionStorage.getItem('login_test_mode') === '1'; } catch (_) { }
        if (params.get('code') && params.get('state') && !(isLoginTestMode || isTestState)) {
          return;
        }

        // 1. userID 확인
        const userID = localStorage.getItem("userID");
        if (!userID) {
          throw new Error("No user ID found");
        }

        // 2. Firestore의 토큰은 아직 활성화하지 않은 후보로 조회한다.
        const {
          getTokenCandidate,
          adoptValidatedToken,
          clearLocalSession,
          setActiveSessionStatus,
        } = await import('./utils/tokenManager');
        setActiveSessionStatus('restoring');
        const tokenCandidate = await getTokenCandidate(userID);
        const validToken = tokenCandidate?.token;

        if (!validToken) {
          throw new Error("No valid token available");
        }

        // 3. 후보 토큰을 /me로 검증한 뒤에만 현재 세션에 채택한다.
        const profileData = await processUserLoginPure(validToken);

        if (String(profileData?.id) !== String(userID)) {
          clearLocalSession();
          const accountMismatchError = new Error('Stored token belongs to a different Threads account');
          accountMismatchError.code = 'TOKEN_ACCOUNT_MISMATCH';
          throw accountMismatchError;
        }

        adoptValidatedToken({ userID, token: validToken });

        // 3.5 Firestore user 문서 upsert (최초 가입 생성, 재방문 업데이트)
        try {
          const userRef = dbService.collection("user").doc(userID);
          const userDoc = await userRef.get();

          if (!userDoc.exists) {
            await userRef.set(
              {
                user_id: profileData.id,
                visitLog: arrayUnion(new Date().toISOString()),
                profile: {
                  ...profileData,
                  signUpDate: new Date().toISOString(),
                },
              },
              { merge: true }
            );
            // 신규 유저는 연말정산 미실시
            dispatch({ type: UserTypes.SET_HAS_YEAR_END, payload: false });
          } else {
            const prev = userDoc.data();
            await userRef.update({
              user_id: profileData.id,
              visitLog: arrayUnion(new Date().toISOString()),
              profile: {
                ...profileData,
                signUpDate: prev?.profile?.signUpDate || new Date().toISOString(),
              },
            });
            // 기존 유저는 yearEndIds 확인
            const yearEndIds = prev?.yearEndIds || {};
            const hasYearEnd = !!yearEndIds['2025'];
            dispatch({ type: UserTypes.SET_HAS_YEAR_END, payload: hasYearEnd });
          }
        } catch (userUpsertError) {
          console.warn('⚠️ Failed to upsert user doc (non-critical):', userUpsertError);
        }

        dispatch({
          type: UserTypes.SET_PROFILE,
          payload: {
            profile: profileData,
            accessToken: validToken, // 유효한 long-lived token 사용
          },
        });

        console.log("✅ Session restored with long-lived token");

      } catch (error) {
        dispatch({
          type: UserTypes.CLEAR_PROFILE,
        });

        // 토큰 관련 에러인 경우 상세 로그 제공
        if (error.message === "No user ID found") {
          console.warn("User not authenticated - no user ID found");
        } else if (error.message === "No valid token available") {
          console.warn("No valid token - user needs to re-authenticate");
        } else if (error.response?.status === 400 || error.response?.status === 401) {
          console.warn("Token validation failed - clearing authentication");
        } else {
          console.error("Error fetching user data:", error);
        }
      }
    };

    loadUserData();

    // 🆕 개발 환경에서 디버깅 도구 설치
    if (process.env.NODE_ENV === 'development') {
      installDebugTools();
      setupGlobalAdminFunctions();
    }
  }, [dispatch]); // dispatch만 의존성으로 설정 (최초 1회만 실행)

  // 라우터 보호 로직은 별도 useEffect로 분리
  useEffect(() => {
    if (!userLoading) {
      const path = location.pathname;
      const isPublic =
        path === "/" ||
        path === "/video-login" ||
        path === "/callback" ||
        path === "/privacy" ||
        path === "/terms" ||
        path === "/admin" || // AdminRoute에서 처리하므로 제외
        path === "/all-collection" ||
        path.startsWith("/collection") ||
        path.startsWith("/user") ||
        path.startsWith("/monthly-threads") ||
        path.startsWith("/threads-cafe-map") ||
        path.startsWith("/people") ||
        path.startsWith("/year-end");

      if (!isLogin && !isPublic) {
        navigate("/");
      }
    }
  }, [isLogin, location.pathname, userLoading, navigate]);

  // Lenis 정리 로직 - Login 페이지가 아닐 때 전역 Lenis 정리
  useEffect(() => {
    const isLoginPage = location.pathname === '/' || location.pathname === '/video-login';

    if (!isLoginPage && window.lenis) {
      // Login 페이지가 아닐 때 전역 Lenis 강제 정리
      if (typeof window.lenis.destroy === 'function') {
        window.lenis.destroy();
      }
      window.lenis = null;
    }
  }, [location.pathname]);

  // ThreadsContext states
  const state = useThreadsState();
  const { isLifetimeLoading, isLifetimeReady, lifetimeThreadsList } = state;
  const threadsDispatch = useThreadsDispatch();

  // 공개 랜딩 라우트와 admin 라우트에서는 글로벌 데이터 로딩 지연.
  // 비공개(내부) 라우트로 진입할 때 한 번만 로딩 시작.
  const isPublicLandingPath = (pathname) => {
    return (
      pathname === "/all-collection" ||
      pathname.startsWith("/collection/") ||
      pathname.startsWith("/monthly-threads") ||
      pathname.startsWith("/threads-cafe-map") ||
      pathname.startsWith("/people")
    );
  };

  const isAdminPath = (pathname) => {
    return pathname.startsWith("/admin");
  };

  useEffect(() => {
    if (!isLogin || !profile || isLifetimeLoading || isLifetimeReady || lifetimeLoadStartedRef.current) {
      return;
    }
    // 공개 랜딩이나 admin 라우트면 대기, 일반 비공개 라우트면 즉시 시작
    if (!isPublicLandingPath(location.pathname) && !isAdminPath(location.pathname)) {
      console.log("🚀 비공개 라우트 접근: 라이프타임 데이터 로드 시작");
      lifetimeLoadStartedRef.current = true;
      const userID = localStorage.getItem("userID");

      // ✅ 개선: 재로그인 시 캐시 우선 사용, 필요시에만 강제 업데이트
      const shouldForceUpdate = false; // 기본적으로 캐시 우선 사용
      loadLifetimeDataWithTimeline(threadsDispatch, userID, profile, shouldForceUpdate);
    }
  }, [isLogin, profile, isLifetimeLoading, isLifetimeReady, location.pathname, threadsDispatch]);


  // 컬렉션 데이터는 MyPage에서 로드하도록 변경

  // Modify the existing location-based useEffect
  useEffect(() => {
    if (isLogin && ["/", "/video-login", "/callback"].includes(location.pathname)) {
      // Check if there's a custom redirect path set
      const hasCustomRedirect = sessionStorage.getItem('login_redirect_to');
      if (!hasCustomRedirect) {
        console.log("✅ User logged in, redirecting to dashboard from:", location.pathname);
        navigate("/dashboard", { replace: true });
      } else {
        console.log("✅ User logged in, custom redirect will be handled by Callback.js");
      }
    }
  }, [location.pathname, isLogin, navigate]);

  // 월별 인사이트 업데이트 로직 제거됨 - Dashboard.js가 timeline으로 직접 관리

  // Add this line after your existing state declarations
  useResetScroll();

  const isCallbackRoute = location.pathname === '/callback';

  // 연말정산 프롬프트 표시 로직
  useEffect(() => {
    // 제외 페이지 확인
    const excludedPaths = ['/year-end', '/', '/video-login', '/callback', '/login'];
    const isExcludedPage = excludedPaths.some(path =>
      location.pathname === path || location.pathname.startsWith('/year-end/')
    );

    // 표시 조건: 로그인 + 미실시 + 허용 페이지 + shouldShow
    if (
      isLogin &&
      hasYearEnd2025 === false && // null(미확인)이 아닌 false일 때만
      !isExcludedPage &&
      shouldShowYearEndPrompt &&
      !isYearEndPromptOpen
    ) {
      // 약간의 딜레이 후 표시 (페이지 로드 완료 대기)
      const timer = setTimeout(() => {
        openYearEndPrompt();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isLogin, hasYearEnd2025, location.pathname, shouldShowYearEndPrompt, isYearEndPromptOpen, openYearEndPrompt]);

  return (
    <>
      {userLoading && !isCallbackRoute ? (
        <Flex justify={"center"} align={"center"} h={"100vh"} w={"100vw"}>
          <Loader />
        </Flex>
      ) : (
        <AppPresenter />
      )}

      {/* 연말정산 프롬프트 모달 */}
      <YearEndPromptModal
        open={isYearEndPromptOpen}
        onClose={dismissYearEndPrompt}
        onPermanentDismiss={permanentlyDismissYearEndPrompt}
      />
    </>
  );
}

export default App;
